import React, { useState, useEffect } from 'react';
import { Loader2 } from "lucide-react";
import { TransactionMatchDialogs } from '@/components/cash/TransactionMatchDialogs';
import { useTransactionDialogs } from '@/hooks/useTransactionDialogs';
import { formatCurrency } from "@/utils/paymentUtils";
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useTransactions } from '@/hooks/useTransactions';
import { useTransactionSyncPolling } from '@/hooks/useTransactionSyncPolling';
import { useWaitingSettlements } from '@/hooks/useWaitingSettlements';
import AccountsSection from '@/components/bank/AccountsSection';
import TaxAccountSection from '@/components/bank/TaxAccountSection';
import TransactionsSection from '@/components/bank/TransactionsSection';
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeaderActions from "@/components/common/PageHeaderActions";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useHighlightTransactionFromUrl } from "@/hooks/useHighlightTransactionFromUrl";
import { BankAccountSummary } from '@/types/accounts';
import { TaxAccountApiClient, type TaxAccountListItem, type TaxConnectContext } from '@/clients/taxAccountApiClient';

const Bank = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslations('consultantTransactions');
  const [refreshing, setRefreshing] = useState(false);

  // Skattekonto (FIN-716). Null — not an empty array — while unknown or unavailable, so the
  // section renders nothing at all rather than "no tax account yet", which would be a claim
  // about this company. Mirrors apps/client's Bank page, which gates the same way.
  const [taxAccounts, setTaxAccounts] = useState<TaxAccountListItem[] | null>(null);
  const [taxConnect, setTaxConnect] = useState<TaxConnectContext | null>(null);

  const loadTaxAccounts = React.useCallback(async () => {
    let accounts: TaxAccountListItem[];
    try {
      accounts = await TaxAccountApiClient.list();
    } catch {
      // ENABLE_TAX_ACCOUNTS off (the edge function 404s) or unreachable. The section simply
      // does not render, and the rest of the page is unaffected — a tax account nobody has
      // connected yet is not an error.
      setTaxAccounts(null);
      setTaxConnect(null);
      return;
    }
    setTaxAccounts(accounts);
    // Needed while nothing is actually readable yet — not only when there are zero accounts.
    // A started-but-unfinished trip and a partial grant both leave a real account behind, and
    // withholding the company details for them leaves the flow with no way to resume.
    const readable = accounts.some((account) => account.status === 'active');
    try {
      setTaxConnect(readable ? null : await TaxAccountApiClient.connectContext());
    } catch {
      // The accounts are real and stay listed; only connecting is withheld, since there is no
      // company to confirm it for. connectContext has already said why (handleError).
      setTaxConnect(null);
    }
  }, []);

  useEffect(() => {
    void loadTaxAccounts();
  }, [loadTaxAccounts]);

  // Check if user came from onboarding (no existing accounts)
  const isOnboarding = location.state?.isOnboarding || false;

  // Get transactionId from URL query parameters if present
  const searchParams = new URLSearchParams(location.search);
  const transactionIdFromUrl = searchParams.get('id');
  
  // Use custom hooks for bank accounts and transactions
  const {
    accounts,
    setAccounts,
    loadingAccounts,
    accountsError,
    loadAccounts,
    handleConnectBank
  } = useBankAccounts();

  // FIN-1021: one hook call, shared by AccountsSection (the banner + per-account map-menu
  // warning) and TransactionsSection (the per-row label) — both read the same summary.
  const {
    summary: waitingSettlements,
    loading: waitingSettlementsLoading,
    loadFailed: waitingSettlementsLoadFailed,
    reload: reloadWaitingSettlements,
  } = useWaitingSettlements();

  const {
    transactions,
    loadingTransactions,
    transactionsError,
    selectedTransactionId,
    setSelectedTransactionId,
    selectedTab,
    setSelectedTab,
    selectedAccounts,
    setSelectedAccounts,
    filters,
    sortedTransactions,
    sortKey,
    sortDirection,
    handleSort,
    renderSortIcon,
    handleFilterChange,
    resetFilters,
    selectedTransaction,
    handleManualMatch,
    handleMatchSelection,
    handleConfirmPayableMatch,
    handleConfirmOwnAccountMatch,
    page,
    pageSize,
    total,
    setPage,
    loadTransactions
  } = useTransactions();

  const dialogs = useTransactionDialogs();

  // After a bank connects, the account is synced in the background (server-side trigger) — poll
  // for completion and refresh the transaction list once it's done (issue #363 follow-up).
  const { pollAccounts } = useTransactionSyncPolling();
  const handleConnectBankAndSync = async (newAccounts: BankAccountSummary[]) => {
    await handleConnectBank(newAccounts);
    pollAccounts(newAccounts.map((a) => a.id), () => {
      loadTransactions();
    });
  };

  // Selecting/deselecting accounts changes the server query (single- vs all-account page), so
  // reset back to the first page to avoid landing past the end of the new result set.
  useEffect(() => {
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccounts]);

  useHighlightTransactionFromUrl(transactionIdFromUrl, transactions, setSelectedTab);

  // Handle opening match modal
  const openMatchModal = (transactionId: string) => {
    handleManualMatch(transactionId);
    dialogs.setMatchOpen(true);
  };

  // Handle opening mark as modal
  const openMarkAsModal = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    dialogs.setMarkAsOpen(true);
  };

  
  // Handle refresh of all data
  const handleRefreshClick = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    toast(t('bank.refreshing'), { description: t('bank.refreshingBody') });
    
    try {
      // Refresh accounts and transactions
      await loadAccounts();
      
      toast.success(t('bank.refreshComplete'), { description: t('bank.refreshCompleteBody') });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error(t('bank.refreshFailed'), {
        description: t('bank.refreshFailedBody'),
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Show loading states
  if (loadingAccounts) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">{t('bank.title')}</h1>
          <PageHeaderActions onRefresh={handleRefreshClick} refreshing={refreshing || loadingAccounts} />
        </div>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-xl">{t('accounts.loading')}</span>
        </div>
      </div>
    );
  }

  if (accountsError) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">{t('bank.title')}</h1>
          <PageHeaderActions onRefresh={handleRefreshClick} refreshing={refreshing} />
        </div>
        <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-600 mb-4">{accountsError}</p>
          <button 
            onClick={loadAccounts}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
          >
            {t('page.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t('bank.title')}</h1>
        <PageHeaderActions onRefresh={handleRefreshClick} refreshing={refreshing} />
      </div>

      {/* Bank Accounts Section */}
      <AccountsSection
        accounts={accounts}
        setAccounts={setAccounts}
        handleConnectBank={handleConnectBankAndSync}
        isOnboarding={isOnboarding}
        onMappingChanged={loadAccounts}
        waitingSettlements={waitingSettlements}
        waitingSettlementsLoading={waitingSettlementsLoading}
        waitingSettlementsLoadFailed={waitingSettlementsLoadFailed}
        reloadWaitingSettlements={reloadWaitingSettlements}
      />

      {/* Tax Account Section (FIN-716) — a peer of the bank accounts, because a tax account
          is the same kind of thing to a consultant reading this page. */}
      <TaxAccountSection
        accounts={taxAccounts}
        onConnected={loadTaxAccounts}
        onAccountsChanged={loadTaxAccounts}
        companyName={taxConnect?.companyName ?? null}
        registrationNumber={taxConnect?.registrationNumber ?? null}
        authorityName={taxConnect?.authorityName ?? null}
        accountHolder={taxConnect?.accountHolder ?? null}
        canConnectTaxAccount={taxConnect?.canConnectTaxAccount ?? false}
        taxConnectReason={taxConnect?.taxConnectReason ?? ''}
        clientUserCount={taxConnect?.clientUserCount ?? 0}
        openOnArrival={location.state?.reconnectTax === true}
      />

      {/* Transactions Section */}
      {(accounts?.length ?? 0) > 0 && (
        <TransactionsSection
          accounts={accounts}
          transactions={transactions}
          loadingTransactions={loadingTransactions}
          transactionsError={transactionsError}
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          filters={filters}
          handleFilterChange={handleFilterChange}
          resetFilters={resetFilters}
          sortedTransactions={sortedTransactions}
          handleManualMatch={openMatchModal}
          handleMarkAs={openMarkAsModal}
          sortKey={sortKey}
          sortDirection={sortDirection}
          handleSort={handleSort}
          renderSortIcon={renderSortIcon}
          formatCurrency={(amount, currency) => formatCurrency(amount, currency ?? 'USD')}
          highlightTransactionId={transactionIdFromUrl}
          page={page}
          pageSize={pageSize}
          total={total}
          setPage={setPage}
          waitingSettlementLabels={waitingSettlements.transactionLabels}
        />
      )}

      <TransactionMatchDialogs
        transaction={selectedTransaction}
        dialogs={dialogs}
        matching={{ handleMatchSelection, handleConfirmPayableMatch, handleConfirmOwnAccountMatch }}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default Bank;
